import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './hooks/useAuth';
import { Toaster } from './components/ui/sonner';
import { LoadingSpinner } from './components/shared/LoadingSpinner';
import { ProtectedUpgradeRoute } from './components/ProtectedUpgradeRoute';
import { ProtectedMasterRoute } from './components/ProtectedMasterRoute';
import { ProtectedOrgAdminRoute } from './components/ProtectedOrgAdminRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { TrelloErrorReport } from './components/TrelloErrorReport';
import { ChatIAWidget } from './components/ChatIAWidget';
import { HelpCenterButton } from './components/HelpCenterButton';
import { GenerationProvider, useGeneration } from './contexts/GenerationContext';
import { proposalService } from './services/proposalService';
import { toast } from 'sonner';

const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite').then(m => ({ default: m.AcceptInvite })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const UpgradePlan = lazy(() => import('./pages/UpgradePlan').then(m => ({ default: m.UpgradePlan })));
const ProposalDetails = lazy(() => import('./pages/ProposalDetails').then(m => ({ default: m.ProposalDetails })));
const Step1Identify = lazy(() => import('./pages/Checkout/Step1Identify').then(m => ({ default: m.Step1Identify })));
const Step2Payment = lazy(() => import('./pages/Checkout/Step2Payment').then(m => ({ default: m.Step2Payment })));
const Success = lazy(() => import('./pages/Checkout/Success').then(m => ({ default: m.Success })));
const OrganizationManagement = lazy(() => import('./pages/OrganizationManagement').then(m => ({ default: m.OrganizationManagement })));
const ConsumoOrganizacaoPage = lazy(() => import('./pages/ConsumoOrganizacaoPage'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Carregando..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Carregando..." />
      </div>
    );
  }

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" text="Carregando..." /></div>}>
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route
          path="/forgot-password"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
        />
        <Route
          path="/reset-password"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPassword />}
        />
        <Route
          path="/accept-invite"
          element={<AcceptInvite />}
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/upgrade-plan"
          element={
            <ProtectedRoute>
              <ProtectedUpgradeRoute>
                <UpgradePlan />
              </ProtectedUpgradeRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/proposals/:id"
          element={
            <ProtectedRoute>
              <ProposalDetails />
            </ProtectedRoute>
          }
        />

        {/* --- NOVAS ROTAS DE CHECKOUT --- */}
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <ProtectedUpgradeRoute>
                <Step1Identify />
              </ProtectedUpgradeRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/payment"
          element={
            <ProtectedRoute>
              <ProtectedUpgradeRoute>
                <Step2Payment />
              </ProtectedUpgradeRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/success"
          element={
            <ProtectedRoute>
              <ProtectedUpgradeRoute>
                <Success />
              </ProtectedUpgradeRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization"
          element={
            <ProtectedRoute>
              <ProtectedMasterRoute>
                <OrganizationManagement />
              </ProtectedMasterRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/consumo-organizacao"
          element={
            <ProtectedRoute>
              <ProtectedOrgAdminRoute>
                <ConsumoOrganizacaoPage />
              </ProtectedOrgAdminRoute>
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
};

const BackgroundGenerationIndicator: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { activeGeneration, endGeneration } = useGeneration();
  const location = useLocation();
  const navigate = useNavigate();

  const isOnProposalPage = activeGeneration
    ? location.pathname === `/proposals/${activeGeneration.proposalId}`
    : false;

  useEffect(() => {
    if (!activeGeneration || isOnProposalPage) return;

    const proposalId = activeGeneration.proposalId;
    const proposalTitle = activeGeneration.proposalTitle;

    const interval = setInterval(async () => {
      try {
        const status = await proposalService.getGenerationStatus(proposalId);
        if (status?.status === 'completed') {
          toast.success(`Geração concluída! "${proposalTitle}" está pronta.`);
          endGeneration();
        } else if (status?.status === 'failed') {
          toast.error(`Geração falhou para "${proposalTitle}".`);
          endGeneration();
        }
      } catch {
        // ignorar erros de polling em background
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [activeGeneration?.proposalId, isOnProposalPage, endGeneration]);

  if (!isAuthenticated || !activeGeneration || isOnProposalPage) return null;

  const displayTitle = activeGeneration.proposalTitle.length > 28
    ? activeGeneration.proposalTitle.slice(0, 28) + '...'
    : activeGeneration.proposalTitle;

  return (
    <div
      onClick={() => navigate(`/proposals/${activeGeneration.proposalId}`)}
      style={{
        position: 'fixed',
        bottom: '20px',
        left: '20px',
        zIndex: 50,
        backgroundColor: '#1f2937',
        color: '#fff',
        borderRadius: '999px',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(0,0,0,0.28)',
        fontSize: '0.875rem',
        fontWeight: 500,
        maxWidth: '280px',
        userSelect: 'none',
      }}
    >
      <span style={{
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: '#10B981',
        flexShrink: 0,
        animation: 'prainte-bg-gen-pulse 1.2s ease-in-out infinite',
      }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        Gerando: {displayTitle}
      </span>
      <style>{`
        @keyframes prainte-bg-gen-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
      `}</style>
    </div>
  );
};

const GlobalChatWidget: React.FC<{ onChatOpenChange?: (isOpen: boolean) => void }> = ({ onChatOpenChange }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const proposalMatch = useMatch('/proposals/:id');

  if (!isAuthenticated) return null;
  if (location.pathname === '/organization') return null;

  const proposalId = proposalMatch ? parseInt(proposalMatch.params.id!, 10) : null;

  return <ChatIAWidget proposalId={proposalId} onOpenChange={onChatOpenChange} />;
};

function App() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <GenerationProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
            <TrelloErrorReport isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} shiftLeft={chatOpen} />
            <HelpCenterButton onOpenFeedback={() => setFeedbackOpen(true)} isOpen={feedbackOpen} />
            <GlobalChatWidget onChatOpenChange={setChatOpen} />
            <BackgroundGenerationIndicator />
          </GenerationProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
